import {Component, Input, OnInit} from '@angular/core';
import {ApiHealthCenterResponse} from '../services/http/api-health-center-response';
import {HealthCenterRestApiService} from '../services/http/health-center-rest-api.service';
import {catchError, debounce, filter, Observable, of, Subject, switchMap, takeUntil, timer} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslocoService} from '@jsverse/transloco';
import {Utils} from '@astek/utils';
import {ApiRefreshTimerEnum} from '../../../core/services/http/api-refresh-timer.enum';
/* eslint-disable max-len */
import {ApiGetHealthCenterSensorsOrdersListPaginatedResponse} from '../services/http/api-get-health-center-sensors-orders-list-paginated-response';
import {ApiGetHealthCenterSensorsOrdersListPaginatedItemResponse} from '../services/http/api-get-health-center-sensors-orders-list-paginated-item-response';
import {ApiGetHealthCenterSensorsOrdersListPaginatedQueryParams} from '../services/http/api-get-health-center-sensors-orders-list-paginated-query-params';
import {PageEvent} from '@angular/material/paginator';
import {MatDialog} from '@angular/material/dialog';
import {SensorOrderShippingDialogComponent} from '../sensor-order-shipping-dialog/sensor-order-shipping-dialog.component';

/* eslint-enable max-len */

@Component({
  selector: 'xp-health-center-orders-list',
  templateUrl: './health-center-orders-list.component.html',
  styleUrls: ['./health-center-orders-list.component.scss'],
})
export class HealthCenterOrdersListComponent implements OnInit {

  @Input()
  public healthCenter!: ApiHealthCenterResponse;
  public isLoading: boolean = true;

  public sensorsOrders: Array<ApiGetHealthCenterSensorsOrdersListPaginatedItemResponse> | null = null;

  public itemsCount: number = 0;
  public pageIndex: number = 0;
  public pageSize: number = 10;

  public displayedColumns: Array<string> = [
    'createdAt',
    'sellingCenter',
    'sensorCount',
    'usedSensorCount',
    'lostSensorCount',
    'lostDelayInDays',
    'stockRatioToNotify',
    'notifiedAt',
    'shippedAt',
  ];

  private _requestApiOnChangeSubject: Subject<number> = new Subject<number>();

  constructor(
    private readonly _healthCenterRestApiService: HealthCenterRestApiService,
    private readonly _snackBar: MatSnackBar,
    private readonly _translocoService: TranslocoService,
    private readonly _dialog: MatDialog,
  ) {
    this._requestApiOnChangeSubject.pipe(
      takeUntilDestroyed(),
      debounce((dueTime: number) => timer(dueTime)),
      switchMap(() => this.requestSensorsOrders().pipe(
        takeUntil(this._requestApiOnChangeSubject),
        catchError(() => {
          this.handleError();
          return of(null);
        }),
      )),
    ).subscribe({
      next: (response: ApiGetHealthCenterSensorsOrdersListPaginatedResponse | null) => {
        if (Utils.isNotNullOrUndefined(response)) {
          this.handleRequestSensorOrdersResponse(response);
        }
      },
    });

  }

  public ngOnInit(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public onPaginatorChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public showShippingDialog(order: ApiGetHealthCenterSensorsOrdersListPaginatedItemResponse): void {
    const dialogRef = this._dialog.open(
      SensorOrderShippingDialogComponent, {panelClass: 'form-dialog'},
    );
    dialogRef.afterClosed().pipe(
      filter((shippedAt: any) => typeof shippedAt === 'string'),
      switchMap((shippedAt: string) => this.requestSensorsOrderShipping(order, shippedAt)),
    ).subscribe({
      next: () => {
        this._snackBar.open(
          this._translocoService.translate('HEALTH_CENTERS.SENSORS_ORDERS.SHIPPING_SUCCESS'),
          '',
          {duration: 7000},
        );
        this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
      },
      error: () => this.handleError(),
    });
  }

  private requestSensorsOrders(): Observable<ApiGetHealthCenterSensorsOrdersListPaginatedResponse> {
    this.isLoading = true;
    const queryParams: ApiGetHealthCenterSensorsOrdersListPaginatedQueryParams = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    };
    return this._healthCenterRestApiService.requestSensorOrders(this.healthCenter.id, queryParams);
  }

  private handleRequestSensorOrdersResponse(response: ApiGetHealthCenterSensorsOrdersListPaginatedResponse): void {
    this.isLoading = false;
    this.sensorsOrders = response.sensorOrders;
    this.itemsCount = response.totalCount;
  }

  private requestSensorsOrderShipping(
    order: ApiGetHealthCenterSensorsOrdersListPaginatedItemResponse,
    shippedAt: string,
  ): Observable<void> {
    return this._healthCenterRestApiService.requestSensorsOrderShipping(order.id, shippedAt);
  }

  private handleError(): void {
    this.isLoading = false;
    this._snackBar.open(
      this._translocoService.translate('SHARED.ERRORS.UNKNOWN'),
      this._translocoService.translate('SHARED.CLOSE'),
    );
  }

}
